import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, HelpCircle } from 'lucide-react';

const faqData = {
  fr: [
    {
      question: "Qu'est-ce que Logirent ?",
      answer: "Logirent est un logiciel SaaS suisse permettant de gérer la location de véhicules, incluant réservation en ligne, contrats digitaux avec signature électronique, facturation automatique et paiement sécurisé (Stripe, TWINT, espèces)."
    },
    {
      question: "Pour qui est Logirent ?",
      answer: "Logirent est conçu pour les entreprises de location de véhicules, garages, concessionnaires, PME avec flotte automobile, et toute organisation gérant des véhicules en location en Suisse romande (Genève, Lausanne, Vaud)."
    },
    {
      question: "Quels problèmes Logirent résout ?",
      answer: "Logirent élimine la gestion manuelle compliquée, les contrats papier, le suivi via Excel. Il automatise les réservations, génère les contrats PDF automatiquement, gère les paiements en ligne et offre un suivi GPS en temps réel de votre flotte."
    },
    {
      question: "Combien coûte Logirent ?",
      answer: "Logirent propose des forfaits à partir de 49 CHF/mois pour les petites flottes (1-10 véhicules), 99 CHF/mois pour les flottes en croissance (11-50 véhicules), et des tarifs sur mesure pour les grandes entreprises (+50 véhicules)."
    },
    {
      question: "Pourquoi choisir Logirent plutôt qu'Excel ?",
      answer: "Contrairement à Excel, Logirent offre une réservation en ligne automatisée 24/7, des contrats avec signature électronique légale, un suivi GPS des véhicules, des paiements intégrés (Stripe, TWINT), et un dashboard analytique en temps réel pour optimiser votre activité."
    },
    {
      question: "Logirent fonctionne-t-il sur mobile ?",
      answer: "Oui, Logirent est disponible en application web responsive (ordinateur, tablette, mobile) et en application mobile Android (APK). Vos clients et votre équipe peuvent accéder à la plateforme depuis n'importe quel appareil."
    },
    {
      question: "Comment fonctionne la signature électronique ?",
      answer: "Logirent génère automatiquement les contrats PDF et permet aux clients de signer électroniquement directement sur leur mobile ou ordinateur. Le contrat signé est envoyé automatiquement par email aux deux parties."
    },
    {
      question: "Puis-je gérer plusieurs agences ?",
      answer: "Oui, Logirent supporte la gestion multi-agences. Chaque agence (Genève, Lausanne, etc.) gère ses propres véhicules, clients et réservations avec un contrôle d'accès par rôle (super admin, admin agence, employé)."
    }
  ],
  en: [
    {
      question: "What is Logirent?",
      answer: "Logirent is a Swiss SaaS software for vehicle rental management, including online booking, digital contracts with e-signature, automatic invoicing and secure payment (Stripe, TWINT, cash)."
    },
    {
      question: "Who is Logirent for?",
      answer: "Logirent is designed for vehicle rental companies, garages, dealerships, SMEs with car fleets, and any organization managing vehicle rentals in French-speaking Switzerland (Geneva, Lausanne, Vaud)."
    },
    {
      question: "What problems does Logirent solve?",
      answer: "Logirent eliminates complicated manual management, paper contracts, Excel tracking. It automates bookings, generates PDF contracts automatically, manages online payments and offers real-time GPS tracking of your fleet."
    },
    {
      question: "How much does Logirent cost?",
      answer: "Logirent offers plans starting at CHF 49/month for small fleets (1-10 vehicles), CHF 99/month for growing fleets (11-50 vehicles), and custom pricing for large companies (+50 vehicles)."
    },
    {
      question: "Why choose Logirent over Excel?",
      answer: "Unlike Excel, Logirent offers 24/7 automated online booking, contracts with legal e-signature, GPS vehicle tracking, integrated payments (Stripe, TWINT), and a real-time analytics dashboard to optimize your business."
    },
    {
      question: "Does Logirent work on mobile?",
      answer: "Yes, Logirent is available as a responsive web application (computer, tablet, mobile) and as an Android mobile app (APK). Your customers and team can access the platform from any device."
    },
    {
      question: "How does electronic signature work?",
      answer: "Logirent automatically generates PDF contracts and allows customers to sign electronically directly on their mobile or computer. The signed contract is automatically sent by email to both parties."
    },
    {
      question: "Can I manage multiple agencies?",
      answer: "Yes, Logirent supports multi-agency management. Each agency (Geneva, Lausanne, etc.) manages its own vehicles, customers and reservations with role-based access control (super admin, agency admin, employee)."
    }
  ]
};

export const FAQ = () => {
  const { language } = useLanguage();
  const [openIndex, setOpenIndex] = useState(null);
  
  const faqs = faqData[language] || faqData.fr;
  
  const titles = {
    fr: {
      title: 'Questions fréquentes',
      subtitle: 'Tout ce que vous devez savoir sur Logirent'
    },
    en: {
      title: 'Frequently asked questions',
      subtitle: 'Everything you need to know about Logirent'
    }
  };
  
  const t = titles[language] || titles.fr;

  return (
    <section id="faq" className="py-16 md:py-24 bg-white" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-xl mb-4">
            <HelpCircle className="w-6 h-6 text-blue-600" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3 tracking-tight">
            {t.title}
          </h2>
          <p className="text-base text-slate-600">
            {t.subtitle}
          </p>
        </motion.div>

        {/* FAQ Items */}
        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="border border-slate-200 rounded-xl overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-slate-50 transition-colors"
                aria-expanded={openIndex === index}
              >
                <span className="font-semibold text-slate-900 pr-4">{faq.question}</span>
                <ChevronDown 
                  className={`w-5 h-5 text-slate-500 flex-shrink-0 transition-transform ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>
              
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="px-5 pb-5 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
